import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useNavigate } from 'react-router-dom';
import { CRITUTILS } from "../../config/API";
import jsPDF from 'jspdf';

const CritBL = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        // Calendar Year
        calendarYear: new Date().getFullYear().toString(),

        // License Type
        licenseType: '',
        temporaryStartDate: '',
        seasonalStartDate: '',
        peddlerType: '',
        expeditedProcessing: false,

        // Reason for Applying
        applicationReason: '',
        previousNameLocation: '',
        ownerChangeDetails: '',

        // Business Information
        legalBusinessName: '',
        federalEIN: '',
        physicalAddress: '',
        mailingAddress: '',
        businessPhone: '',
        businessFax: '',
        businessEmail: '',
        commenceDate: '',
        businessDescription: '',
        businessActivity: '',
        tobaccoSales: '',
        liquorSales: '',
        previousLicense: '',
        previousLicenseNumber: '',
        previousBusinessName: '',
        licenseSuspended: '',
        licenseSuspendedDetails: '',
        entityType: '',
        incorporationState: '',

        // Owner Information
        owners: [{ name: '', percentage: '' }],
        ownerMailingAddress: '',
        ownerPhone: '',
        ownerFax: '',
        ownerEmail: '',
        primaryContactFirstName: '',
        primaryContactLastName: '',
        primaryContactDOB: '',
        primaryContactPhone: '',
        primaryContactEmail: '',

        // Additional Business Information
        agentName: '',
        agentAddress: '',
        critLease: '',
        leaseNumber: '',
        leaseAddress: '',
        critMember: '',
        enrollmentNumber: '',
        otherTribe: '',
        otherTribeName: '',

        // Consent
        signature: '',
        printName: '',
        signatureDate: '',
        agentSignature: '',
        agentSignatureDate: ''
    });

    const [loading, setLoading] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const [error, setError] = useState('');
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [submittedData, setSubmittedData] = useState(null); // Store submitted data for PDF

    // Refs for signature pads
    const signaturePadRef = useRef(null);
    const agentSignaturePadRef = useRef(null);

    // US Phone Number Formatting Function
    const formatPhoneNumber = (value) => {
        // Remove all non-digits
        const cleaned = value.replace(/\D/g, '');

        // Limit to 10 digits
        const limited = cleaned.slice(0, 10);

        // Apply formatting based on length
        if (limited.length <= 3) {
            return limited;
        } else if (limited.length <= 6) {
            return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
        } else {
            return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
        }
    };

    // Auto-copy function for business information
    const handleBusinessInfoChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Apply phone number formatting for specific fields
        if (name === 'businessPhone' || name === 'businessFax' ||
            name === 'ownerPhone' || name === 'ownerFax' ||
            name === 'primaryContactPhone') {
            const formattedValue = formatPhoneNumber(value);
            setFormData(prev => ({
                ...prev,
                [name]: formattedValue
            }));

            // Auto-copy business phone to owner phone and primary contact phone
            if (name === 'businessPhone' && formattedValue) {
                setFormData(prev => ({
                    ...prev,
                    ownerPhone: formattedValue,
                    primaryContactPhone: formattedValue
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));

            // Auto-copy business email to owner email and primary contact email
            if (name === 'businessEmail' && value) {
                setFormData(prev => ({
                    ...prev,
                    ownerEmail: value,
                    primaryContactEmail: value
                }));
            }

            // Auto-copy physical address to owner mailing address
            if (name === 'physicalAddress' && value) {
                setFormData(prev => ({
                    ...prev,
                    ownerMailingAddress: value
                }));
            }
        }
    };

    const handleInputChange = (e) => {
        handleBusinessInfoChange(e);
    };

    const handleOwnerChange = (index, field, value) => {
        const updatedOwners = [...formData.owners];
        updatedOwners[index] = { ...updatedOwners[index], [field]: value };
        setFormData(prev => ({ ...prev, owners: updatedOwners }));
    };

    const addOwner = () => {
        setFormData(prev => ({
            ...prev,
            owners: [...prev.owners, { name: '', percentage: '' }]
        }));
    };

    const removeOwner = (index) => {
        if (formData.owners.length > 1) {
            const updatedOwners = formData.owners.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, owners: updatedOwners }));
        }
    };

    // Handle signature clear
    const clearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            setFormData(prev => ({ ...prev, signature: '' }));
        }
    };

    // Handle agent signature clear
    const clearAgentSignature = () => {
        if (agentSignaturePadRef.current) {
            agentSignaturePadRef.current.clear();
            setFormData(prev => ({ ...prev, agentSignature: '' }));
        }
    };

    // Handle signature end (when user finishes signing)
    const handleSignatureEnd = () => {
        if (signaturePadRef.current) {
            const signatureData = signaturePadRef.current.toDataURL();
            setFormData(prev => ({ ...prev, signature: signatureData }));
        }
    };

    // Handle agent signature end
    const handleAgentSignatureEnd = () => {
        if (agentSignaturePadRef.current) {
            const signatureData = agentSignaturePadRef.current.toDataURL();
            setFormData(prev => ({ ...prev, agentSignature: signatureData }));
        }
    };

    const calculateFee = (data = formData) => {
        let fee = 0;

        switch (data.licenseType) {
            case 'Temporary': fee += 50; break;
            case 'Seasonal': fee += 75; break;
            case 'Annual': fee += 125; break;
            case 'peddler-retail': fee += 100; break;
            case 'peddler-food': fee += 150; break;
            default: break;
        }

        if (data.expeditedProcessing) fee += 30;
        if (data.applicationReason === 'change-name') fee += 100;
        if (data.applicationReason === 'change-owner') fee += 100;

        return fee;
    };

    const validateForm = () => {
        console.log(formData);
        if (!formData.calendarYear) {
            setError('Calendar year is required.');
            return false;
        }
        if (!formData.licenseType) {
            setError('Please select a license type.');
            return false;
        }
        if (!formData.legalBusinessName) {
            setError('Legal business name is required.');
            return false;
        }
        if (!formData.federalEIN) {
            setError('Federal EIN or social security number is required.');
            return false;
        }
        if (!formData.physicalAddress) {
            setError('Physical business address is required.');
            return false;
        }
        if (!formData.signature) {
            setError('Signature is required. Please sign in the "Please sign here" section.');
            return false;
        }
        if (!formData.printName) {
            setError('Printed name is required.');
            return false;
        }
        if (!formData.signatureDate) {
            setError('Signature date is required.');
            return false;
        }

        // Validate phone number format (optional but should be valid if provided)
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
        if (formData.businessPhone && !phoneRegex.test(formData.businessPhone)) {
            setError('Business telephone number must be in format (XXX) XXX-XXXX.');
            return false;
        }
        if (formData.ownerPhone && !phoneRegex.test(formData.ownerPhone)) {
            setError('Owner telephone number must be in format (XXX) XXX-XXXX.');
            return false;
        }
        if (formData.primaryContactPhone && !phoneRegex.test(formData.primaryContactPhone)) {
            setError('Primary contact telephone number must be in format (XXX) XXX-XXXX.');
            return false;
        }

        return true;
    };

    // Convert data URL to blob for file upload
    const dataURLtoBlob = (dataURL) => {
        const byteString = atob(dataURL.split(',')[1]);
        const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return new Blob([ab], { type: mimeString });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitStatus('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            // Create FormData for multipart form data
            const formDataToSend = new FormData();

            // Add all form fields
            formDataToSend.append('calendarYear', formData.calendarYear);
            formDataToSend.append('licensetype', formData.licenseType.toLowerCase());
            formDataToSend.append('temporaryStartDate', formData.temporaryStartDate);
            formDataToSend.append('seasonalStartDate', formData.seasonalStartDate);
            formDataToSend.append('peddlertype', formData.peddlerType);
            formDataToSend.append('expeditedProcessing', formData.expeditedProcessing.toString());
            formDataToSend.append('applicationReason', formData.applicationReason);
            formDataToSend.append('previousNameLocation', formData.previousNameLocation);
            formDataToSend.append('ownerChangeDetails', formData.ownerChangeDetails);
            formDataToSend.append('legalBusinessName', formData.legalBusinessName);
            formDataToSend.append('federalEIN', formData.federalEIN);
            formDataToSend.append('physicalAddress', formData.physicalAddress);
            formDataToSend.append('mailingAddress', formData.mailingAddress);
            formDataToSend.append('businessPhone', formData.businessPhone);
            formDataToSend.append('businessFax', formData.businessFax);
            formDataToSend.append('businessEmail', formData.businessEmail);
            formDataToSend.append('commenceDate', formData.commenceDate);
            formDataToSend.append('businessDescription', formData.businessDescription);
            formDataToSend.append('businessActivity', formData.businessActivity);
            formDataToSend.append('tobaccoSales', formData.tobaccoSales);
            formDataToSend.append('liquorSales', formData.liquorSales);
            formDataToSend.append('previousLicense', formData.previousLicense);
            formDataToSend.append('previousLicenseNumber', formData.previousLicenseNumber);
            formDataToSend.append('previousBusinessName', formData.previousBusinessName);
            formDataToSend.append('licenseSuspended', formData.licenseSuspended);
            formDataToSend.append('licenseSuspendedDetails', formData.licenseSuspendedDetails);
            formDataToSend.append('entityType', formData.entityType);
            formDataToSend.append('incorporationState', formData.incorporationState);
            formDataToSend.append('owners', JSON.stringify(formData.owners.filter(owner => owner.name && owner.percentage)));
            formDataToSend.append('ownerMailingAddress', formData.ownerMailingAddress);
            formDataToSend.append('ownerPhone', formData.ownerPhone);
            formDataToSend.append('ownerFax', formData.ownerFax);
            formDataToSend.append('ownerEmail', formData.ownerEmail);
            formDataToSend.append('primaryContactFirstName', formData.primaryContactFirstName);
            formDataToSend.append('primaryContactLastName', formData.primaryContactLastName);
            formDataToSend.append('primaryContactDOB', formData.primaryContactDOB);
            formDataToSend.append('primaryContactPhone', formData.primaryContactPhone);
            formDataToSend.append('primaryContactEmail', formData.primaryContactEmail);
            formDataToSend.append('agentName', formData.agentName);
            formDataToSend.append('agentAddress', formData.agentAddress);
            formDataToSend.append('critLease', formData.critLease);
            formDataToSend.append('leaseNumber', formData.leaseNumber);
            formDataToSend.append('leaseAddress', formData.leaseAddress);
            formDataToSend.append('critMember', formData.critMember);
            formDataToSend.append('enrollmentNumber', formData.enrollmentNumber);
            formDataToSend.append('otherTribe', formData.otherTribe);
            formDataToSend.append('otherTribeName', formData.otherTribeName);
            formDataToSend.append('printName', formData.printName);
            formDataToSend.append('signatureDate', formData.signatureDate);
            formDataToSend.append('agentSignatureDate', formData.agentSignatureDate);
            formDataToSend.append('totalFee', calculateFee().toString());
            formDataToSend.append('submissionDate', new Date().toISOString());

            // Add signature files as multipart data
            if (formData.signature) {
                const signatureBlob = dataURLtoBlob(formData.signature);
                formDataToSend.append('signature', signatureBlob, 'signature.png');
            }

            if (formData.agentSignature) {
                const agentSignatureBlob = dataURLtoBlob(formData.agentSignature);
                formDataToSend.append('agentSignature', agentSignatureBlob, 'agent_signature.png');
            }

            const apiUrl = CRITUTILS + `?action=blapply`;

            const response = await fetch(apiUrl, {
                method: 'POST',
                body: formDataToSend
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log(result);

            // Check if response indicates success based on the API response format
            if (result.Status === "Success") {
                setSubmitStatus('success');
                // Store the submitted data before resetting
                setSubmittedData({ ...formData });
                setShowSuccessScreen(true);

                // Reset form
                setFormData({
                    calendarYear: new Date().getFullYear().toString(),
                    licenseType: '',
                    temporaryStartDate: '',
                    seasonalStartDate: '',
                    peddlerType: '',
                    expeditedProcessing: false,
                    applicationReason: '',
                    previousNameLocation: '',
                    ownerChangeDetails: '',
                    legalBusinessName: '',
                    federalEIN: '',
                    physicalAddress: '',
                    mailingAddress: '',
                    businessPhone: '',
                    businessFax: '',
                    businessEmail: '',
                    commenceDate: '',
                    businessDescription: '',
                    businessActivity: '',
                    tobaccoSales: '',
                    liquorSales: '',
                    previousLicense: '',
                    previousLicenseNumber: '',
                    previousBusinessName: '',
                    licenseSuspended: '',
                    licenseSuspendedDetails: '',
                    entityType: '',
                    incorporationState: '',
                    owners: [{ name: '', percentage: '' }],
                    ownerMailingAddress: '',
                    ownerPhone: '',
                    ownerFax: '',
                    ownerEmail: '',
                    primaryContactFirstName: '',
                    primaryContactLastName: '',
                    primaryContactDOB: '',
                    primaryContactPhone: '',
                    primaryContactEmail: '',
                    agentName: '',
                    agentAddress: '',
                    critLease: '',
                    leaseNumber: '',
                    leaseAddress: '',
                    critMember: '',
                    enrollmentNumber: '',
                    otherTribe: '',
                    otherTribeName: '',
                    signature: '',
                    printName: '',
                    signatureDate: '',
                    agentSignature: '',
                    agentSignatureDate: ''
                });

                // Clear signature pads
                if (signaturePadRef.current) signaturePadRef.current.clear();
                if (agentSignaturePadRef.current) agentSignaturePadRef.current.clear();

            } else {
                setError(result.Message || 'Submission failed. Please try again.');
            }

        } catch (error) {
            console.error('Error submitting form:', error);
            setError('An error occurred while submitting the form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Success Screen Component with PDF Download
    const SuccessScreen = () => {
        const generatePDF = () => {
            if (!submittedData) {
                console.error('No submitted data available for PDF generation.');
                alert('No data available for PDF generation. Please try submitting the form again.');
                return;
            }

            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            let yPosition = 15;

            // Format date to mm/dd/yyyy (US). For YYYY-MM-DD strings, avoid Date() so the day does not shift in US timezones.
            const formatDate = (dateString) => {
                if (!dateString) return '';
                try {
                    const trimmed = String(dateString).trim();
                    const dateOnly = trimmed.indexOf(' ') >= 0 ? trimmed.split(' ')[0] : trimmed;
                    const parts = dateOnly.split(/[-\/]/);
                    if (parts.length === 3) {
                        if (parts[0].length === 4) {
                            return `${parts[1].padStart(2, '0')}/${parts[2].padStart(2, '0')}/${parts[0]}`;
                        }
                        return dateOnly;
                    }
                    const date = new Date(dateString);
                    if (isNaN(date.getTime())) return trimmed;
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${month}/${day}/${year}`;
                } catch (e) {
                    return dateString;
                }
            };

            // Set font and colors
            pdf.setFont('helvetica');
            pdf.setTextColor(0, 0, 0); // #840028

            // Header Section
            pdf.setFontSize(12);
            pdf.text('COLORADO RIVER INDIAN TRIBES', pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 5;
            pdf.setFontSize(10);
            pdf.text('Department of Revenue and Finance', pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 5;
            pdf.setFontSize(9);
            pdf.text('Business License Application Summary', pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 8;

            // Calendar Year
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Calendar Year: ${submittedData.calendarYear}`, 15, yPosition);
            yPosition += 6;

            // License Information
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            pdf.text('LICENSE INFORMATION', 15, yPosition);
            yPosition += 5;
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);

            // License Type
            let licenseTypeText = 'Not specified';
            switch (submittedData.licenseType) {
                case 'Temporary':
                    licenseTypeText = `Temporary - $50.00`;
                    break;
                case 'Seasonal':
                    licenseTypeText = `Seasonal - $75.00`;
                    break;
                case 'Annual':
                    licenseTypeText = 'Annual - $125.00';
                    break;
                case 'peddler-retail':
                    licenseTypeText = 'Peddler Retail - $100.00';
                    break;
                case 'peddler-food':
                    licenseTypeText = 'Peddler Food - $150.00';
                    break;
                default:
                    licenseTypeText = 'Not specified';
            }
            pdf.text(`License Type: ${licenseTypeText}`, 18, yPosition);
            yPosition += 4;

            // License Dates
            if (submittedData.temporaryStartDate) {
                pdf.text(`Temporary Start Date: ${formatDate(submittedData.temporaryStartDate)}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.seasonalStartDate) {
                pdf.text(`Seasonal Start Date: ${formatDate(submittedData.seasonalStartDate)}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.expeditedProcessing) {
                pdf.text(`Expedited Processing: Yes - $30.00`, 18, yPosition);
                yPosition += 4;
            }

            // Reason for Applying
            if (submittedData.applicationReason) {
                let reasonText = '';
                switch (submittedData.applicationReason) {
                    case 'new-existing':
                        reasonText = 'New applicant (existing business)';
                        break;
                    case 'new-startup':
                        reasonText = 'New applicant (started new business)';
                        break;
                    case 'renewal':
                        reasonText = 'License renewal';
                        break;
                    case 'change-name':
                        reasonText = 'Change name of business/location';
                        break;
                    case 'change-owner':
                        reasonText = 'Add/remove owner/partner';
                        break;
                    default:
                        reasonText = submittedData.applicationReason;
                }
                pdf.text(`Application Reason: ${reasonText}`, 18, yPosition);
                yPosition += 4;
            }

            if (submittedData.previousNameLocation) {
                pdf.text(`Previous Name/Location: ${submittedData.previousNameLocation}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.ownerChangeDetails) {
                pdf.text(`Owner Change Details: ${submittedData.ownerChangeDetails}`, 18, yPosition);
                yPosition += 4;
            }

            yPosition += 3;

            // Business Information
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            pdf.text('BUSINESS INFORMATION', 15, yPosition);
            yPosition += 5;
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);

            // Business Details - Column 1
            const businessCol1 = [
                { field: 'Legal Business Name', value: submittedData.legalBusinessName },
                { field: 'Federal EIN/SSN', value: submittedData.federalEIN },
                { field: 'Physical Address', value: submittedData.physicalAddress },
                { field: 'Mailing Address', value: submittedData.mailingAddress || 'Same as physical' },
                { field: 'Business Phone', value: submittedData.businessPhone },
                { field: 'Business Fax', value: submittedData.businessFax }
            ];

            businessCol1.forEach(item => {
                if (item.value && yPosition < pageHeight - 30) {
                    const text = `${item.field}: ${item.value}`;
                    if (pdf.getTextWidth(text) > (pageWidth - 36)) {
                        // Split long text into multiple lines
                        const lines = pdf.splitTextToSize(text, pageWidth - 36);
                        lines.forEach(line => {
                            if (yPosition < pageHeight - 30) {
                                pdf.text(line, 18, yPosition);
                                yPosition += 4;
                            }
                        });
                    } else {
                        pdf.text(text, 18, yPosition);
                        yPosition += 4;
                    }
                }
            });

            // Business Details - Column 2
            yPosition = 65; // Reset to same starting point for second column
            const businessCol2 = [
                { field: 'Business Email', value: submittedData.businessEmail },
                { field: 'Commence Date', value: formatDate(submittedData.commenceDate) },
                { field: 'Business Description', value: submittedData.businessDescription },
                { field: 'Business Activity', value: submittedData.businessActivity },
                { field: 'Entity Type', value: submittedData.entityType },
                { field: 'Incorporation State', value: submittedData.incorporationState }
            ];

            businessCol2.forEach(item => {
                if (item.value && yPosition < pageHeight - 30) {
                    const text = `${item.field}: ${item.value}`;
                    if (pdf.getTextWidth(text) > (pageWidth - 106)) {
                        const lines = pdf.splitTextToSize(text, pageWidth - 106);
                        lines.forEach(line => {
                            if (yPosition < pageHeight - 30) {
                                pdf.text(line, 100, yPosition);
                                yPosition += 4;
                            }
                        });
                    } else {
                        pdf.text(text, 100, yPosition);
                        yPosition += 4;
                    }
                }
            });

            // Business Activities
            yPosition = 89;
            yPosition += 3;

            if (submittedData.tobaccoSales) {
                pdf.text(`Tobacco Sales: ${submittedData.tobaccoSales}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.liquorSales) {
                pdf.text(`Liquor Sales: ${submittedData.liquorSales}`, 18, yPosition);
                yPosition += 4;
            }

            // License History
            if (submittedData.previousLicense) {
                pdf.text(`Previous Tribal License: ${submittedData.previousLicense === 'yes' ? 'Yes' : 'No'}`, 18, yPosition);
                yPosition += 4;
                if (submittedData.previousLicense === 'yes' && submittedData.previousLicenseNumber) {
                    pdf.text(`Previous License Number: ${submittedData.previousLicenseNumber}`, 18, yPosition);
                    yPosition += 4;
                }
            }

            if (submittedData.previousBusinessName) {
                pdf.text(`Previous Business Name: ${submittedData.previousBusinessName === 'yes' ? 'Yes' : 'No'}`, 18, yPosition);
                yPosition += 4;
            }

            if (submittedData.licenseSuspended) {
                pdf.text(`License Ever Suspended: ${submittedData.licenseSuspended === 'yes' ? 'Yes' : 'No'}`, 18, yPosition);
                yPosition += 4;
                if (submittedData.licenseSuspended === 'yes' && submittedData.licenseSuspendedDetails) {
                    const suspensionLines = pdf.splitTextToSize(`Suspension Details: ${submittedData.licenseSuspendedDetails}`, pageWidth - 36);
                    suspensionLines.forEach(line => {
                        if (yPosition < pageHeight - 30) {
                            pdf.text(line, 18, yPosition);
                            yPosition += 4;
                        }
                    });
                }
            }

            yPosition += 3;

            // Owner Information
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            pdf.text('OWNER INFORMATION', 15, yPosition);
            yPosition += 5;
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);

            // List all owners
            submittedData.owners.forEach((owner, index) => {
                if (owner.name && yPosition < pageHeight - 30) {
                    pdf.text(`Owner ${index + 1}: ${owner.name} - ${owner.percentage}% Ownership`, 18, yPosition);
                    yPosition += 4;
                }
            });

            // Owner Contact Information
            if (submittedData.ownerMailingAddress) {
                pdf.text(`Owner Mailing Address: ${submittedData.ownerMailingAddress}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.ownerPhone) {
                pdf.text(`Owner Phone: ${submittedData.ownerPhone}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.ownerFax) {
                pdf.text(`Owner Fax: ${submittedData.ownerFax}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.ownerEmail) {
                pdf.text(`Owner Email: ${submittedData.ownerEmail}`, 18, yPosition);
                yPosition += 4;
            }

            // Primary Contact
            if (submittedData.primaryContactFirstName || submittedData.primaryContactLastName) {
                const primaryContactName = `${submittedData.primaryContactFirstName || ''} ${submittedData.primaryContactLastName || ''}`.trim();
                if (primaryContactName) {
                    pdf.text(`Primary Contact Name: ${primaryContactName}`, 18, yPosition);
                    yPosition += 4;
                }
            }
            if (submittedData.primaryContactDOB) {
                pdf.text(`Primary Contact DOB: ${formatDate(submittedData.primaryContactDOB)}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.primaryContactPhone) {
                pdf.text(`Primary Contact Phone: ${submittedData.primaryContactPhone}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.primaryContactEmail) {
                pdf.text(`Primary Contact Email: ${submittedData.primaryContactEmail}`, 18, yPosition);
                yPosition += 4;
            }

            yPosition += 3;

            // Additional Business Information
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            pdf.text('ADDITIONAL INFORMATION', 15, yPosition);
            yPosition += 5;
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);

            if (submittedData.agentName) {
                pdf.text(`Agent Name: ${submittedData.agentName}`, 18, yPosition);
                yPosition += 4;
            }
            if (submittedData.agentAddress) {
                const agentAddressLines = pdf.splitTextToSize(`Agent Address: ${submittedData.agentAddress}`, pageWidth - 36);
                agentAddressLines.forEach(line => {
                    if (yPosition < pageHeight - 30) {
                        pdf.text(line, 18, yPosition);
                        yPosition += 4;
                    }
                });
            }

            if (submittedData.critLease) {
                pdf.text(`CRIT Lease: ${submittedData.critLease === 'yes' ? 'Yes' : 'No'}`, 18, yPosition);
                yPosition += 4;
                if (submittedData.critLease === 'yes') {
                    if (submittedData.leaseNumber) {
                        pdf.text(`Lease Number: ${submittedData.leaseNumber}`, 18, yPosition);
                        yPosition += 4;
                    }
                    if (submittedData.leaseAddress) {
                        pdf.text(`Lease Address: ${submittedData.leaseAddress}`, 18, yPosition);
                        yPosition += 4;
                    }
                }
            }

            if (submittedData.critMember) {
                pdf.text(`CRIT Tribal Member: ${submittedData.critMember === 'yes' ? 'Yes' : 'No'}`, 18, yPosition);
                yPosition += 4;
                if (submittedData.critMember === 'yes' && submittedData.enrollmentNumber) {
                    pdf.text(`Enrollment Number: ${submittedData.enrollmentNumber}`, 18, yPosition);
                    yPosition += 4;
                }
            }

            if (submittedData.otherTribe) {
                pdf.text(`Other Tribe Member: ${submittedData.otherTribe === 'yes' ? 'Yes' : 'No'}`, 18, yPosition);
                yPosition += 4;
                if (submittedData.otherTribe === 'yes' && submittedData.otherTribeName) {
                    pdf.text(`Other Tribe Name: ${submittedData.otherTribeName}`, 18, yPosition);
                    yPosition += 4;
                }
            }

            yPosition += 3;

            // Signature Information
            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);
            pdf.text('SIGNATURE INFORMATION', 15, yPosition);
            yPosition += 5;
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);

            // Applicant Signature
            pdf.text(`Applicant Printed Name: ${submittedData.printName}`, 18, yPosition);
            yPosition += 4;
            pdf.text(`Applicant Signature Date: ${formatDate(submittedData.signatureDate)}`, 18, yPosition);
            yPosition += 4;

            if (submittedData.signature) {
                try {
                    pdf.text('Applicant Signature:', 18, yPosition);
                    yPosition += 8;
                    // Add signature image
                    pdf.addImage(submittedData.signature, 'PNG', 18, yPosition, 50, 20);
                    yPosition += 25;
                } catch (error) {
                    pdf.text('Applicant Signature: Provided (image error)', 18, yPosition);
                    yPosition += 4;
                }
            } else {
                pdf.text('Applicant Signature: Not provided', 18, yPosition);
                yPosition += 4;
            }

            yPosition += 3;

            // Agent Signature
            if (submittedData.agentSignature || submittedData.agentName) {
                if (submittedData.agentName) {
                    pdf.text(`Agent Name: ${submittedData.agentName}`, 18, yPosition);
                    yPosition += 4;
                }

                if (submittedData.agentSignature) {
                    try {
                        pdf.text('Agent Signature:', 18, yPosition);
                        yPosition += 8;
                        pdf.addImage(submittedData.agentSignature, 'PNG', 18, yPosition, 50, 20);
                        yPosition += 25;
                    } catch (error) {
                        pdf.text('Agent Signature: Provided (image error)', 18, yPosition);
                        yPosition += 4;
                    }
                } else {
                    pdf.text('Agent Signature: Not provided', 18, yPosition);
                    yPosition += 4;
                }

                if (submittedData.agentSignatureDate) {
                    pdf.text(`Agent Signature Date: ${formatDate(submittedData.agentSignatureDate)}`, 18, yPosition);
                    yPosition += 4;
                }
            }

            // Fee Summary at bottom
            yPosition = pageHeight - 15;
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            pdf.text('TOTAL APPLICATION FEE:', 20, yPosition);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`$${calculateFee(submittedData)}.00`, 70, yPosition);

            // Submission info
            pdf.setFontSize(7);
            pdf.setTextColor(0, 0, 0);
            pdf.text(`Submitted on: ${formatDate(new Date())}`, pageWidth - 10, yPosition, { align: 'right' });

            // Save the PDF
            const fileName = `CRIT_Business_License_Application_${submittedData.legalBusinessName || 'application'}_${submittedData.calendarYear}.pdf`;
            pdf.save(fileName);
        };

        const handleClose = () => {
            // Close the current tab
            window.close();

            // Fallback - if window.close doesn't work, go to home
            setTimeout(() => {
                navigate('/home');
            }, 100);
        };

        return (
            <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
                <div className="max-w-md mx-auto bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200 text-center">
                    <div className="bg-[#840028] text-white p-6">
                        <h1 className="text-2xl font-bold">Colorado River Indian Tribes</h1>
                    </div>

                    <div className="p-8">
                        <div className="flex justify-center mb-6">
                            <svg className="h-16 w-16 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-800 mb-4">Application Submitted Successfully!</h3>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <p className="text-green-800 font-semibold">
                                Your business license application has been submitted successfully.
                            </p>

                            <p className="text-green-700 text-sm">
                                Reference: <span className="font-bold">Business License Application</span>.
                            </p>
                        </div>

                        <p className="text-gray-600 mb-6 text-sm">
                            Thank you for submitting your business license application.
                            Your application has been received and is being processed.
                            You can download a detailed PDF copy for your records.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={generatePDF}
                                className="bg-blue-600 text-white px-2 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md flex items-center justify-center gap-2"
                            >
                                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download Business License Application PDF
                            </button>

                            <button
                                onClick={handleClose}
                                className="bg-[#840028] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6a0020] transition duration-200 shadow-md"
                            >
                                Close
                            </button>
                        </div>

                        <p className="text-xs text-gray-500 mt-4">
                            PDF includes all application details with signatures and field values.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    // If showing success screen, return only the success screen
    if (showSuccessScreen) {
        return <SuccessScreen />;
    }

    return (
        <div className="min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200">
                {/* Header */}
                <div className="bg-[#840028] text-white p-6">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold tracking-tight">Colorado River Indian Tribes</h1>
                        <h2 className="text-xl font-semibold mt-1">Department of Revenue and Finance</h2>
                        <p className="text-sm mt-3 opacity-90">1000 West Agency Road, Suite A, Parker, Arizona 85344</p>
                        <p className="text-sm opacity-90">Ph (928) 575-1532 • Fax (928) 575-1540 • taxinfo@crit-nsn.gov</p>
                    </div>
                </div>

                <div className="p-6">
                    <h1 className="text-3xl font-bold text-center mb-6 text-gray-800 tracking-tight">
                        Application for Business License
                    </h1>

                    {/* Calendar Year Field - Added at the top */}
                    <div className="mb-6 flex justify-center">
                        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 inline-block">
                            <label className="text-lg font-bold text-gray-800 mr-3">
                                For Calendar Year:
                            </label>
                            <input
                                type="text"
                                name="calendarYear"
                                value={formData.calendarYear}
                                onChange={handleInputChange}
                                className="w-24 border border-gray-300 rounded px-3 py-2 text-center font-bold text-gray-800 focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                placeholder="YYYY"
                                required
                            />
                            <span className="text-sm text-gray-600 ml-2">(must indicate year)</span>
                        </div>
                    </div>

                    {/* Status Messages */}
                    {submitStatus === 'success' && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="text-green-800 font-semibold">
                                    Your application has been submitted successfully!
                                </p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <svg className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <p className="text-red-800 font-semibold">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-amber-800 font-semibold">
                                    Notice: Application must be completed in its entirety and signed.
                                    No modifications of any kind may be made to this application form.
                                    Payments received are non-refundable.
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* License Type Section */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Type of license requested (Check only one)
                            </h2>

                            <div className="space-y-4">
                                {/* Temporary License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Temporary"
                                        checked={formData.licenseType === 'Temporary'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-800">Temporary – Engaged in business four (4) days or less</label>
                                        <p className="text-sm text-gray-600 mt-1">Fee: $50.00</p>
                                        <div className="mt-2">
                                            <label className="text-sm text-gray-700">Applicant must indicate date business is to commence:</label>
                                            <input
                                                type="date"
                                                name="temporaryStartDate"
                                                value={formData.temporaryStartDate}
                                                onChange={handleInputChange}
                                                className="ml-2 border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Seasonal License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Seasonal"
                                        checked={formData.licenseType === 'Seasonal'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-800">Seasonal – Engaged in business more than 4 days but less than 3 months</label>
                                        <p className="text-sm text-gray-600 mt-1">Fee: $75.00</p>
                                        <div className="mt-2">
                                            <label className="text-sm text-gray-700">Applicant must indicate date business is to commence:</label>
                                            <input
                                                type="date"
                                                name="seasonalStartDate"
                                                value={formData.seasonalStartDate}
                                                onChange={handleInputChange}
                                                className="ml-2 border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Annual License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="Annual"
                                        checked={formData.licenseType === 'Annual'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div>
                                        <label className="font-semibold text-gray-800">Annual – Engaged in business more than 3 months</label>
                                        <p className="text-sm text-gray-600 mt-1">(Expires December 31st of each year of issuance) Fee: $125.00</p>
                                    </div>
                                </div>

                                {/* Peddler License */}
                                <div className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="radio"
                                        name="licenseType"
                                        value="peddler-retail"
                                        checked={formData.licenseType === 'peddler-retail'}
                                        onChange={handleInputChange}
                                        className="mt-1 text-[#840028] focus:ring-[#840028]"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-800">Peddler – Must meet the definition of peddler per business & prof. code</label>
                                        <p className="text-sm text-gray-600 mt-1">(Expires December 31st of each year of issuance)</p>
                                        <div className="mt-2 space-y-2 ml-4">
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name="licenseType"
                                                    value="peddler-retail"
                                                    checked={formData.licenseType === 'peddler-retail'}
                                                    onChange={handleInputChange}
                                                    className="text-[#840028] focus:ring-[#840028]"
                                                />
                                                <label className="text-gray-700">Retail sales (non-food) Fee: $100.00</label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="radio"
                                                    name="licenseType"
                                                    value="peddler-food"
                                                    checked={formData.licenseType === 'peddler-food'}
                                                    onChange={handleInputChange}
                                                    className="text-[#840028] focus:ring-[#840028]"
                                                />
                                                <label className="text-gray-700">Retail sales (food) Fee: $150.00</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Expedited Processing */}
                                <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-gray-200 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <input
                                        type="checkbox"
                                        name="expeditedProcessing"
                                        checked={formData.expeditedProcessing}
                                        onChange={handleInputChange}
                                        className="rounded text-[#840028] focus:ring-[#840028]"
                                    />
                                    <label className="font-semibold text-gray-800">Expedited processing – Next business day processing</label>
                                    <span className="text-sm text-gray-600 ml-2">Fee: $30.00</span>
                                </div>
                            </div>
                        </section>

                        {/* Reason for Applying Section */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Reason for applying
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="new-existing" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">New applicant (existing business)</label>
                                    </div>
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="new-startup" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">New applicant (started new business)</label>
                                    </div>
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="renewal" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">License renewal</label>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="change-name" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">Change name of business / location (Fee: $100.00)</label>
                                    </div>
                                    {formData.applicationReason === 'change-name' && (
                                        <input
                                            type="text"
                                            name="previousNameLocation"
                                            value={formData.previousNameLocation}
                                            onChange={handleInputChange}
                                            placeholder="Previous name/location"
                                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                        />
                                    )}
                                    <div className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input type="radio" name="applicationReason" value="change-owner" onChange={handleInputChange} className="text-[#840028] focus:ring-[#840028]" />
                                        <label className="text-gray-700">Add/remove owner/partner (Fee: $100.00)</label>
                                    </div>
                                    {formData.applicationReason === 'change-owner' && (
                                        <input
                                            type="text"
                                            name="ownerChangeDetails"
                                            value={formData.ownerChangeDetails}
                                            onChange={handleInputChange}
                                            placeholder="Written authorization required from all owner(s)"
                                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#840028] focus:border-[#840028]"
                                        />
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Business Information Section */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Business information
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Field 1: Legal Business Name */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        1. Legal business name:
                                    </label>
                                    <input
                                        type="text"
                                        name="legalBusinessName"
                                        value={formData.legalBusinessName}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 2: Federal EIN or SSN */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        2. Federal EIN or social security number:
                                    </label>
                                    <input
                                        type="text"
                                        name="federalEIN"
                                        value={formData.federalEIN}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 3: Physical Business Address */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        3. Physical business address: (City, State and Zip)
                                    </label>
                                    <input
                                        type="text"
                                        name="physicalAddress"
                                        value={formData.physicalAddress}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        required
                                    />
                                </div>

                                {/* Field 4: Mailing Address */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        4. Mailing address (if different):
                                    </label>
                                    <input
                                        type="text"
                                        name="mailingAddress"
                                        value={formData.mailingAddress}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 5: Business Telephone and Fax */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        5. Business telephone number:
                                    </label>
                                    <input
                                        type="tel"
                                        name="businessPhone"
                                        value={formData.businessPhone}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Business fax number:
                                    </label>
                                    <input
                                        type="tel"
                                        name="businessFax"
                                        value={formData.businessFax}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 6: Business Email */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        6. Business email address:
                                    </label>
                                    <input
                                        type="email"
                                        name="businessEmail"
                                        value={formData.businessEmail}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 7: Commence Date */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        7. Date business is to commence:
                                    </label>
                                    <input
                                        type="date"
                                        name="commenceDate"
                                        value={formData.commenceDate}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>

                                {/* Field 8: Business Description */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        8. Business description:
                                    </label>
                                    <div className="flex flex-wrap gap-4">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="businessDescription"
                                                value="sales"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Sales of goods</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="businessDescription"
                                                value="services"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Services</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="businessDescription"
                                                value="both"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Goods and services</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Field 9: Business Activity */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        9. Describe type of business activity to be conducted:
                                    </label>
                                    <textarea
                                        name="businessActivity"
                                        value={formData.businessActivity}
                                        onChange={handleInputChange}
                                        rows={3}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        placeholder="Must include type of merchandise sold or service provided"
                                    />
                                </div>

                                {/* Field 10: Tobacco Sales */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        10. If tobacco is sold:
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="tobaccoSales"
                                                value="retailer"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Retailer</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="tobaccoSales"
                                                value="distributor"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Distributor</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Field 11: Liquor Sales */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        11. If liquor is sold:
                                    </label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="liquorSales"
                                                value="retailer"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Retailer</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="liquorSales"
                                                value="distributor"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Distributor</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Field 12: Previous Tribal License */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        12. Has your business been issued a tribal business license in the past 5 years?
                                    </label>
                                    <div className="flex gap-4 mb-3">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="previousLicense"
                                                value="yes"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="previousLicense"
                                                value="no"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">No</span>
                                        </label>
                                    </div>
                                    {formData.previousLicense === 'yes' && (
                                        <input
                                            type="text"
                                            name="previousLicenseNumber"
                                            value={formData.previousLicenseNumber}
                                            onChange={handleInputChange}
                                            placeholder="BL#"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    )}
                                </div>

                                {/* Field 13: Previous Business Name */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        13. Have you previously applied for a tribal business license under another name?
                                    </label>
                                    <div className="flex gap-4 mb-3">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="previousBusinessName"
                                                value="yes"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="previousBusinessName"
                                                value="no"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">No</span>
                                        </label>
                                    </div>
                                    {formData.previousBusinessName === 'yes' && (
                                        <input
                                            type="text"
                                            name="previousNameLocation"
                                            value={formData.previousNameLocation}
                                            onChange={handleInputChange}
                                            placeholder="Please write name"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    )}
                                </div>

                                {/* Field 14: License Suspended/Revoked */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        14. Has any business license you held ever been suspended, revoked or application denied?
                                    </label>
                                    <div className="flex gap-4 mb-3">
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="licenseSuspended"
                                                value="yes"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">Yes</span>
                                        </label>
                                        <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                            <input
                                                type="radio"
                                                name="licenseSuspended"
                                                value="no"
                                                onChange={handleInputChange}
                                                className="text-[#840028] focus:ring-[#840028]"
                                            />
                                            <span className="text-gray-700">No</span>
                                        </label>
                                    </div>
                                    {formData.licenseSuspended === 'yes' && (
                                        <textarea
                                            name="licenseSuspendedDetails"
                                            value={formData.licenseSuspendedDetails}
                                            onChange={handleInputChange}
                                            rows={3}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                            placeholder="Please explain"
                                        />
                                    )}
                                </div>

                                {/* Field 15: Type of Entity */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                                        15. Type of entity:
                                    </label>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {[
                                            'Sole proprietorship/individual',
                                            'Limited liability company',
                                            'Educational organization',
                                            'Partnership',
                                            'Limited liability partnership',
                                            'Church organization',
                                            'Corporation',
                                            'Unincorporated association',
                                            'Joint venture',
                                            'Non-profit corporation',
                                            'Professional limited liability',
                                            'Tax exempt organization (501(c)(3))'
                                        ].map((entity) => (
                                            <label key={entity} className="flex items-center space-x-2 text-sm p-2 hover:bg-gray-50 rounded">
                                                <input
                                                    type="radio"
                                                    name="entityType"
                                                    value={entity.toLowerCase().replace(/\s+/g, '-')}
                                                    onChange={handleInputChange}
                                                    className="text-[#840028] focus:ring-[#840028]"
                                                />
                                                <span className="text-gray-700">{entity}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {formData.entityType === 'tax-exempt-organization-(501(c)(3))' && (
                                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                            <p className="text-sm text-blue-800">
                                                Notice: Applicant must provide 26 U.S. C. sec. 501 (c)(3) documentation as proof of tax-exempt status.
                                                If not provided, applicant must pay applicable fee.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Field 16: Incorporation State */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        16. If a corporation, indicate state where incorporated:
                                    </label>
                                    <input
                                        type="text"
                                        name="incorporationState"
                                        value={formData.incorporationState}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Owner and Primary Contact Information */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Owner and primary contact information (Required)
                            </h2>

                            {/* Field 17: List All Owners */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    17. List all owner(s) and % of ownership or corporation name:
                                    <span className="text-xs text-gray-500 ml-1">(If additional space is needed, attach a separate sheet)</span>
                                </label>
                                <div className="space-y-3">
                                    {formData.owners.map((owner, index) => (
                                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                            <input
                                                type="text"
                                                value={owner.name}
                                                onChange={(e) => handleOwnerChange(index, 'name', e.target.value)}
                                                placeholder="Owner name or corporation name"
                                                className="flex-1 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                            />
                                            <input
                                                type="text"
                                                value={owner.percentage}
                                                onChange={(e) => handleOwnerChange(index, 'percentage', e.target.value)}
                                                placeholder="% Ownership"
                                                className="w-32 border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                            />
                                            {formData.owners.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeOwner(index)}
                                                    className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 transition-colors"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addOwner}
                                        className="bg-[#840028] text-white px-4 py-2 rounded hover:bg-[#6a0020] transition-colors font-semibold"
                                    >
                                        Add Owner
                                    </button>
                                </div>
                            </div>

                            {/* Field 18: Owner Mailing Address */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    18. Owner(s) mailing address (City, State, Zip)
                                </label>
                                <input
                                    type="text"
                                    name="ownerMailingAddress"
                                    value={formData.ownerMailingAddress}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                />
                            </div>

                            {/* Field 19: Owner Contact Information */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Owner(s) telephone
                                    </label>
                                    <input
                                        type="tel"
                                        name="ownerPhone"
                                        value={formData.ownerPhone}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Owner(s) fax:
                                    </label>
                                    <input
                                        type="tel"
                                        name="ownerFax"
                                        value={formData.ownerFax}
                                        onChange={handleInputChange}
                                        placeholder="(XXX) XXX-XXXX"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Owner(s) email
                                    </label>
                                    <input
                                        type="email"
                                        name="ownerEmail"
                                        value={formData.ownerEmail}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                </div>
                            </div>

                            {/* Field 20: Primary Contact */}
                            <div className="space-y-4 mb-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Primary contact first name:
                                        </label>
                                        <input
                                            type="text"
                                            name="primaryContactFirstName"
                                            value={formData.primaryContactFirstName}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Primary contact last name:
                                        </label>
                                        <input
                                            type="text"
                                            name="primaryContactLastName"
                                            value={formData.primaryContactLastName}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Primary contact DOB:
                                        </label>
                                        <input
                                            type="date"
                                            name="primaryContactDOB"
                                            value={formData.primaryContactDOB}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Telephone:
                                        </label>
                                        <input
                                            type="tel"
                                            name="primaryContactPhone"
                                            value={formData.primaryContactPhone}
                                            onChange={handleInputChange}
                                            placeholder="(XXX) XXX-XXXX"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Email:
                                        </label>
                                        <input
                                            type="email"
                                            name="primaryContactEmail"
                                            value={formData.primaryContactEmail}
                                            onChange={handleInputChange}
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Additional Business Information */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Additional business information
                            </h2>

                            {/* Field 21: Agent for Service of Process */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    21. Agent for service of process – Provide name and address of agent who will accept process on behalf of business:
                                    <span className="text-xs text-gray-500 ml-1">(If no agent, leave blank)</span>
                                </label>
                                <textarea
                                    name="agentAddress"
                                    value={formData.agentAddress}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    placeholder="Name and address of agent"
                                />
                            </div>

                            {/* Field 22: CRIT Lease/Contract */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    22. CRIT lease / contract
                                </label>
                                <div className="flex gap-4 mb-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critLease"
                                            value="yes"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critLease"
                                            value="no"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                                {formData.critLease === 'yes' && (
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            name="leaseNumber"
                                            value={formData.leaseNumber}
                                            onChange={handleInputChange}
                                            placeholder="Lease No. or Contract No."
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                        <input
                                            type="text"
                                            name="leaseAddress"
                                            value={formData.leaseAddress}
                                            onChange={handleInputChange}
                                            placeholder="Address listed on Lease or Contract"
                                            className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Field 23: CRIT Tribal Member */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    23. CRIT tribal member?
                                </label>
                                <div className="flex gap-4 mb-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critMember"
                                            value="yes"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="critMember"
                                            value="no"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                                {formData.critMember === 'yes' && (
                                    <input
                                        type="text"
                                        name="enrollmentNumber"
                                        value={formData.enrollmentNumber}
                                        onChange={handleInputChange}
                                        placeholder="Enrollment No."
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                )}
                            </div>

                            {/* Field 24: Other Tribe */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    24. Enrolled at another Indian tribe?
                                </label>
                                <div className="flex gap-4 mb-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="otherTribe"
                                            value="yes"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">Yes</span>
                                    </label>
                                    <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                                        <input
                                            type="radio"
                                            name="otherTribe"
                                            value="no"
                                            onChange={handleInputChange}
                                            className="text-[#840028] focus:ring-[#840028]"
                                        />
                                        <span className="text-gray-700">No</span>
                                    </label>
                                </div>
                                {formData.otherTribe === 'yes' && (
                                    <input
                                        type="text"
                                        name="otherTribeName"
                                        value={formData.otherTribeName}
                                        onChange={handleInputChange}
                                        placeholder="Name of tribe"
                                        className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                    />
                                )}
                            </div>
                        </section>

                        {/* Consent and Verification */}
                        <section className="border border-gray-300 rounded-lg p-6 bg-white shadow-sm">
                            <h2 className="text-xl font-bold mb-4 text-gray-800 border-b border-gray-200 pb-3">
                                Consent and verification
                            </h2>

                            {/* Field 25: Consent Text */}
                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    25. By signing below, I understand and agree to the following conditions of license:
                                </label>
                                <div className="bg-gray-50 p-4 rounded border border-gray-200 text-sm space-y-3">
                                    <p className="text-gray-700">
                                        Each licensee shall comply with all tribal laws, including but not limited to: tribal tax laws,
                                        Indian employment and contracting preference laws and applicable federal law. The licensee is
                                        required to comply with any additional tribal laws as such laws are enacted by the Tribal Council,
                                        and obtain any other licenses or permits required by applicable law.
                                    </p>
                                    <p className="text-gray-700">
                                        Each licensee consents to the jurisdiction of the Tribal Court as to any cause of action arising
                                        in connection with the transaction of any business within the reservation, or any tortious acts
                                        committed in connection with the transaction of any business within the reservation. Each licensee
                                        consents to the service of process of the Tribal Court with respect to all actions over which the
                                        Tribal Court has subject matter jurisdiction, in accordance with the rules of procedure of the Tribal Court.
                                    </p>
                                    <p className="text-gray-700">
                                        Each licensee shall respond in a timely manner to requests by the Department of Revenue for information
                                        about the licensee's business for the purpose of establishing whether the licensee is in compliance with
                                        the terms of the Business and Professions Code.
                                    </p>
                                    <p className="font-semibold text-gray-800">
                                        I declare that I have examined this application and the information contained herein and to the best of
                                        my knowledge and belief, it is true and correct. I swear or affirm that I will comply with all tribal
                                        laws applicable to my business and consent to the jurisdiction of the Tribal Court of the Colorado River
                                        Indian Tribes and service of process in matters arising from the conduct of business.
                                    </p>
                                </div>
                            </div>

                            {/* Signature Section */}
                            <div>
                                <p className="font-semibold mb-4 text-gray-800">Application must be signed by an authorized person:</p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Applicant Signature */}
                                    <div className="border border-gray-300 rounded-lg p-4 bg-white">
                                        <div className="text-center mb-3">
                                            <span className="text-sm font-semibold text-gray-700">Please sign here *</span>
                                            <span className="text-red-500 ml-1">(Required)</span>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="border border-gray-300 rounded bg-white shadow-sm">
                                                <SignatureCanvas
                                                    ref={signaturePadRef}
                                                    penColor="#0000FF"
                                                    canvasProps={{
                                                        width: 400,
                                                        height: 150,
                                                        className: 'w-full h-40 border-b border-gray-300 signature-canvas'
                                                    }}
                                                    onEnd={handleSignatureEnd}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <button
                                                    type="button"
                                                    onClick={clearSignature}
                                                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                                                >
                                                    Clear signature
                                                </button>
                                                <span className="text-xs text-gray-500">Sign above - Required</span>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Date: *</label>
                                                <input
                                                    type="date"
                                                    name="signatureDate"
                                                    value={formData.signatureDate}
                                                    onChange={handleInputChange}
                                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Print name: *</label>
                                                <input
                                                    type="text"
                                                    name="printName"
                                                    value={formData.printName}
                                                    onChange={handleInputChange}
                                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Agent Signature */}
                                    <div className="border border-gray-300 rounded-lg p-4 bg-white">
                                        <div className="text-center mb-3">
                                            <span className="text-sm font-semibold text-gray-700">Agent for service of process:</span>
                                            <span className="text-gray-500 ml-1">(Optional)</span>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="border border-gray-300 rounded bg-white shadow-sm">
                                                <SignatureCanvas
                                                    ref={agentSignaturePadRef}
                                                    penColor="#0000FF"
                                                    canvasProps={{
                                                        width: 400,
                                                        height: 150,
                                                        className: 'w-full h-40 border-b border-gray-300 signature-canvas'
                                                    }}
                                                    onEnd={handleAgentSignatureEnd}
                                                />
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <button
                                                    type="button"
                                                    onClick={clearAgentSignature}
                                                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                                                >
                                                    Clear signature
                                                </button>
                                                <span className="text-xs text-gray-500">Sign above (if applicable)</span>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Date:</label>
                                                <input
                                                    type="date"
                                                    name="agentSignatureDate"
                                                    value={formData.agentSignatureDate}
                                                    onChange={handleInputChange}
                                                    className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-[#840028] focus:border-[#840028] transition-colors"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Final Notice */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700 font-semibold">
                                        Notice: Any modifications to this application form or its provisions shall be cause to deny the application,
                                        revoke the license or assess penalties. All fees paid are non-refundable. This application and all information
                                        filed with or received by the department shall become the property of the department. Documents or other
                                        information provided to the department will not be returned to the applicant.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Fee Summary */}
                        <section className="bg-gray-100 rounded-lg p-6 border border-gray-300">
                            <h2 className="text-xl font-bold mb-4 text-gray-800">Fee summary</h2>
                            <div className="bg-white rounded-lg p-4 border border-gray-300 shadow-sm">
                                <p className="text-lg font-semibold text-gray-800">Total fee: <span className="text-[#840028]">${calculateFee()}.00</span></p>
                            </div>
                        </section>

                        {/* Submit Button */}
                        <div className="flex justify-center">
                            <button
                                type="submit"
                                disabled={loading}
                                className="bg-[#840028] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6a0020] transition duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Submitting...' : 'Submit Application'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CritBL;